import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, ShieldCheck, MousePointer2, Heart, X, Sparkles, MapPin, Home, Wallet, Zap } from "lucide-react";

const PROPERTY_IMG =
  "https://images.unsplash.com/photo-1613553507747-5f8d62ad5904?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsJTIwbW9kZXJuJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmUlMjB2aWxsYXxlbnwwfHx8fDE3OTM3OTQ1Mjl8MA&ixlib=rb-4.1.0&q=85";
const SEEKER_AVATAR =
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjBzbWlsaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkzNzk0NTI5fDA&ixlib=rb-4.1.0&q=85&w=80";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const SpecRow = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2.5">
    <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-slate-100">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
    </span>
    <p className="text-[12.5px] font-semibold text-[#0A192F]">{children}</p>
  </div>
);

const TinderMatchVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative hidden lg:block"
      style={{ perspective: 1000 }}
      data-testid="hero-match-visual"
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => { mx.set(0); my.set(0); }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative w-[420px] xl:w-[470px] h-[540px]"
      >
        <div className="absolute -inset-6 bg-[#10B981]/10 blur-3xl rounded-full" aria-hidden="true" />

        {/* Seeker request card */}
        <motion.div
          initial={{ opacity: 0, x: -50, rotate: -8 }}
          animate={{ opacity: 1, x: 0, rotate: -3 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-2 left-0 z-10 w-[265px] xl:w-[285px] bg-white rounded-3xl p-5 border border-slate-200/90 shadow-[0_15px_35px_rgba(10,25,47,0.08)] hover:rotate-0 transition-transform duration-300"
          data-testid="seeker-request-card"
        >
          <div className="flex items-center gap-2.5 mb-4">
            <img src={SEEKER_AVATAR} alt="Richiedente" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" />
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-[#0A192F] leading-tight truncate">Marco &amp; Giulia P.</p>
              <span
                className="inline-flex items-center gap-1 mt-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
                data-testid="verified-lead-pill"
              >
                <BadgeCheck className="w-3 h-3" /> Richiesta verificata
              </span>
            </div>
          </div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">La loro richiesta</p>
          <div className="space-y-2.5">
            <SpecRow icon={MapPin}>Milano · Brera / Navigli</SpecRow>
            <SpecRow icon={Home}>Trilocale · min 85 m² + terrazzo</SpecRow>
            <SpecRow icon={Wallet}>Budget max € 450.000</SpecRow>
            <SpecRow icon={Zap}>Pronti al rogito in 60 gg</SpecRow>
          </div>
        </motion.div>

        {/* Agency offer card */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotate: 8 }}
          animate={{ opacity: 1, x: 0, rotate: 3 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-14 right-0 z-20 w-[275px] xl:w-[300px] bg-white rounded-3xl overflow-hidden border border-slate-200/90 shadow-[0_25px_50px_rgba(10,25,47,0.14)] hover:rotate-0 transition-transform duration-300"
          data-testid="agency-offer-card"
        >
          <div className="relative">
            <img src={PROPERTY_IMG} alt="Attico trilocale vista Brera" className="w-full h-36 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 via-transparent to-transparent" />
            <p className="absolute bottom-2.5 left-3.5 text-white font-display font-extrabold text-lg drop-shadow">€ 430.000</p>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <BadgeCheck className="w-3.5 h-3.5 text-[#10B981]" />
              <p className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">Immobiliare Brera · Verificata</p>
            </div>
            <p className="text-[14px] font-bold text-[#0A192F] leading-snug">Attico trilocale con terrazzo vista Brera</p>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {["92 m²", "3 locali", "Piano 4", "Terrazzo 24 m²"].map((s) => (
                <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10.5px] font-semibold text-slate-600">{s}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Match badge */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.5, type: "spring", stiffness: 260, damping: 14 }}
          className="absolute z-30 top-[46%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
          data-testid="match-percentage-badge"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-[#10B981]/25 blur-xl rounded-full animate-pulse" aria-hidden="true" />
            <div className="relative bg-[#10B981] text-white font-black text-[13px] px-4 py-2 rounded-full shadow-[0_10px_25px_rgba(16,185,129,0.45)] flex items-center gap-1.5 border-2 border-white">
              <Heart className="w-4 h-4 fill-white" />
              98% MATCH!
            </div>
          </div>
        </motion.div>

        {/* Tinder action bar */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full border border-slate-200/80 shadow-lg"
        >
          <button
            type="button"
            data-testid="tinder-action-skip-btn"
            aria-label="Salta"
            className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <X className="w-4.5 h-4.5" />
          </button>
          <button
            type="button"
            aria-label="Super match"
            className="w-10 h-10 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center hover:bg-amber-100 transition-colors"
          >
            <Sparkles className="w-4.5 h-4.5" />
          </button>
          <a
            href="#richiesta"
            data-testid="tinder-action-match-btn"
            className="px-4 h-10 rounded-full bg-[#10B981] text-white text-[13px] font-bold flex items-center gap-2 hover:bg-[#059669] transition-colors shadow-md"
          >
            <Heart className="w-4 h-4 fill-white" />
            Sblocca contatto
          </a>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden grain pt-36 pb-20 sm:pt-44 sm:pb-28">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 10%, rgba(16,185,129,0.10) 0%, transparent 60%), radial-gradient(50% 40% at 10% 90%, rgba(10,25,47,0.06) 0%, transparent 60%), linear-gradient(to bottom, #ffffff 0%, #F8F9FA 100%)",
        }}
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 mb-8"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                Matching immobiliare inverso · 100% gratuito
              </span>
            </motion.div>

            <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.05]">
              <MaskedLine delay={0.2}>Basta cercare casa.</MaskedLine>
              <MaskedLine delay={0.32}>Da oggi è l'immobile</MaskedLine>
              <MaskedLine delay={0.44}>
                a <span className="text-[#10B981] italic">fare match con te!</span>
              </MaskedLine>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-400"
              >
                Il Tinder degli immobili: vendita e affitto
              </motion.p>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Appartamento, villa, terreno, capannone o negozio: dichiari una sola volta cosa cerchi,
              quanto spendi e dove. Poi <strong className="text-[#0A192F] font-semibold">sono agenzie e proprietari
              a contattarti solo se hanno il match giusto</strong> — anche immobili mai pubblicati online.
              E scopri chi, vedendo la tua richiesta, decide di vendere o affittare proprio a te.{" "}
              <strong className="text-[#0A192F] font-semibold">Zero giri di agenzie, zero telefonate a vuoto.</strong>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <a
                href="#richiesta"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#10B981] text-white font-bold px-8 py-4 text-base shadow-[0_8px_25px_rgba(16,185,129,0.35)] hover:bg-[#059669] hover:shadow-[0_12px_32px_rgba(16,185,129,0.45)] hover:-translate-y-0.5 transition-all duration-300"
              >
                Attiva il matching gratis
                <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <span className="text-sm text-slate-500 font-medium">60 secondi · zero spam · zero costi</span>
            </motion.div>
          </div>

          <TinderMatchVisual />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-20 flex items-center gap-3 text-slate-400"
        >
          <MousePointer2 className="w-4 h-4 animate-bounce" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em]">Scorri e compila la tua richiesta</span>
        </motion.div>
      </div>
    </section>
  );
};
